/* AppleFX v8 real-time 3D (three.js, loaded on demand from jsDelivr).
   A physically lit device: titanium frame, black glass, gloss, studio reflections; its screen is the REAL panel
   UI (crisp 3x textures captured from the AppleFX 1.2.5 build), never AI-drawn.
   Mount: <div data-afx3d='{"mode":"hero"|"layers", ...}'> (see sections/afx8-hero.liquid / afx8-layers.liquid).
   hero   : tall panel on the right, floating glass UI cards, pointer tilt, idle float, entrance.
   layers : the panel's real layers as glass slabs that dock as the section scrolls (progress in el.__p, 0..1).
   No WebGL / load error -> el gets .afx3d-fail and its <img> fallback stays. Renders only while on screen. */
(function () {
  'use strict';
  if (window.__afx3d) return; window.__afx3d = 1;
  var THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.170.0/build/three.module.min.js';
  var tp = null;
  /* Shopify's CDN minifier rewrites a literal dynamic import() into require(), so three.js is pulled in by an inline
     module script instead; it hands the namespace back through window.__afxThree. */
  function three() {
    if (tp) return tp;
    tp = new Promise(function (res, rej) {
      if (window.__afxThree) return res(window.__afxThree);
      window.addEventListener('afxthree', function () { res(window.__afxThree); }, { once: true });
      var s = document.createElement('script'); s.type = 'module';
      s.textContent = "import * as T from '" + THREE_URL + "';window.__afxThree=T;window.dispatchEvent(new Event('afxthree'));";
      document.head.appendChild(s);
      setTimeout(function () { if (!window.__afxThree) rej(new Error('three.js did not load')); }, 20000);
    });
    return tp;
  }
  var REDUCED = window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches;
  function clamp(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }
  function ease(t) { return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function outCubic(t) { return 1 - Math.pow(1 - t, 3); }

  /* ---------- geometry ---------- */
  function rr(T, w, h, r) {
    var s = new T.Shape(), x = -w / 2, y = -h / 2; r = Math.max(1e-4, Math.min(r, w / 2, h / 2));
    s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
    s.lineTo(x + w, y + h - r); s.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
    s.lineTo(x + r, y + h); s.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
    s.lineTo(x, y + r); s.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
    return s;
  }
  function flat(T, w, h, r) {
    var g = new T.ShapeGeometry(rr(T, w, h, r), 28), p = g.attributes.position, uv = g.attributes.uv;
    for (var i = 0; i < p.count; i++) uv.setXY(i, p.getX(i) / w + .5, p.getY(i) / h + .5);
    uv.needsUpdate = true; return g;
  }
  function slab(T, w, h, r, d, bev) {
    bev = Math.min(bev, d / 2.05, r * .95, w / 4, h / 4);
    var g = new T.ExtrudeGeometry(rr(T, w - 2 * bev, h - 2 * bev, Math.max(1e-3, r - bev)), {
      depth: Math.max(1e-4, d - 2 * bev), bevelEnabled: true, bevelThickness: bev, bevelSize: bev, bevelSegments: 7, curveSegments: 40
    });
    g.center(); return g;
  }

  /* ---------- studio environment (PMREM of a soft-box room; gives the frame and glass real reflections) ---------- */
  function studioEnv(T, R, dark, acc) {
    var s = new T.Scene();
    var room = new T.Mesh(new T.BoxGeometry(14, 9, 14), new T.MeshBasicMaterial({ color: dark ? 0x23272e : 0x59606c, side: T.BackSide }));
    room.position.y = 1.2; s.add(room);
    var floor = new T.Mesh(new T.PlaneGeometry(14, 14), new T.MeshBasicMaterial({ color: 0x2c3038 }));
    floor.rotation.x = -Math.PI / 2; floor.position.y = -3.2; s.add(floor);
    function L(w, h, p, col, k) {
      var m = new T.Mesh(new T.PlaneGeometry(w, h), new T.MeshBasicMaterial({ color: new T.Color(col).multiplyScalar(k), side: T.DoubleSide }));
      m.position.set(p[0], p[1], p[2]); m.lookAt(0, 0, 0); s.add(m);
    }
    L(9, 3.2, [0, 5.6, .5], 0xffffff, dark ? 1.6 : 3.4);      // big top soft box
    L(2.4, 7, [-6.8, 1.2, 2], 0xffffff, 2.6);    // left strip
    L(2.2, 7, [6.8, 1.2, -1.5], 0xe4edff, 2.2);  // right strip, cool
    L(6, 2.4, [0, .8, 6.8], 0xffffff, 1.5);      // front fill
    L(1.1, 6, [4.6, .2, 6.2], acc || 0x2f86ff, 2.8);    // rim accent (brand colour, c.accent)
    L(1.1, 6, [-5.2, .4, -5.4], acc ? 0xd8dccf : 0x8fb6ff, 1.6);  // back kicker
    var pm = new T.PMREMGenerator(R), t = pm.fromScene(s, .035).texture; pm.dispose(); return t;
  }

  function blobShadow(T, col) {
    col = col || '8,28,70';
    var c = document.createElement('canvas'); c.width = c.height = 256;
    var x = c.getContext('2d'), g = x.createRadialGradient(128, 128, 0, 128, 128, 128);
    g.addColorStop(0, 'rgba(' + col + ',.5)'); g.addColorStop(.45, 'rgba(' + col + ',.2)'); g.addColorStop(1, 'rgba(' + col + ',0)');
    x.fillStyle = g; x.fillRect(0, 0, 256, 256);
    var t = new T.CanvasTexture(c); t.colorSpace = T.SRGBColorSpace;
    return new T.Mesh(new T.PlaneGeometry(1, 1), new T.MeshBasicMaterial({ map: t, transparent: true, depthWrite: false, toneMapped: false }));
  }

  /* key light that rotates with the object: casts the soft (VSM) shadows and puts a moving highlight on the metal */
  function keyLight(T, parent, ext, pos, k) {
    /* shadow-only light: its direct specular would put a hot spot on the glossy glass, so it is nearly dark;
       ShadowMaterial shadows do not depend on the intensity */
    var L = new T.DirectionalLight(0xffffff, k == null ? .002 : k); L.position.set(pos[0], pos[1], pos[2]);
    L.castShadow = true; L.shadow.mapSize.set(2048, 2048); L.shadow.radius = 9; L.shadow.blurSamples = 18; L.shadow.bias = -.0004;
    var c = L.shadow.camera; c.left = -ext; c.right = ext; c.top = ext; c.bottom = -ext; c.near = .1; c.far = 40; c.updateProjectionMatrix();
    parent.add(L); parent.add(L.target); L.target.position.set(0, 0, 0); return L;
  }
  function casts(g) { g.traverse(function (m) { if (m.isMesh) m.castShadow = true; }); }

  /* ---------- stage: renderer, camera, visibility-gated loop ---------- */
  function stage(T, el, c) {
    var canvas = document.createElement('canvas'); canvas.className = 'afx3d-c';
    el.appendChild(canvas);
    var R = new T.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance', preserveDrawingBuffer: !!el.hasAttribute('data-afx3d-shot') });
    var small = el.clientWidth < 700;
    R.setPixelRatio(Math.min(window.devicePixelRatio || 1, small ? 1.75 : 2));
    R.outputColorSpace = T.SRGBColorSpace; R.toneMapping = T.ACESFilmicToneMapping; R.toneMappingExposure = 1.04;
    R.setClearColor(0x000000, 0);
    R.shadowMap.enabled = true; R.shadowMap.type = T.VSMShadowMap;
    var scene = new T.Scene(), env = studioEnv(T, R, c && c.envDark, c && c.accent);
    /* envMapIntensity is only honoured for a material's own envMap (r163+), so every material gets it explicitly */
    function E(m) { m.envMap = env; return m; }
    var cam = new T.PerspectiveCamera(26, 1, .1, 200);
    var loader = new T.TextureLoader(); loader.setCrossOrigin('anonymous');
    var aniso = R.capabilities.getMaxAnisotropy();
    function tex(url) {
      return new Promise(function (res, rej) {
        loader.load(url, function (t) { t.colorSpace = T.SRGBColorSpace; t.anisotropy = aniso; t.minFilter = T.LinearMipmapLinearFilter; res(t); }, undefined, rej);
      });
    }
    var fns = [], fitFns = [], vis = true, raf = 0, last = 0, firstDone = false;
    function size() {
      var w = el.clientWidth, h = el.clientHeight; if (!w || !h) return;
      R.setSize(w, h, false); cam.aspect = w / h; cam.updateProjectionMatrix(); fitFns.forEach(function (f) { f(w, h); });
      if (!raf) R.render(scene, cam);
    }
    function loop(t) {
      raf = 0; if (!vis) return;
      var dt = last ? Math.min(.05, (t - last) / 1000) : .016; last = t;
      fns.forEach(function (f) { f(t / 1000, dt); });
      R.render(scene, cam);
      if (!firstDone) { firstDone = true; el.classList.add('afx3d-on'); }
      raf = requestAnimationFrame(loop);
    }
    function go() { if (!raf && vis) { last = 0; raf = requestAnimationFrame(loop); } }
    if (window.ResizeObserver) new ResizeObserver(size).observe(el); else window.addEventListener('resize', size);
    if ('IntersectionObserver' in window) new IntersectionObserver(function (es) { vis = es[es.length - 1].isIntersecting && !document.hidden; go(); }, { rootMargin: '120px 0px' }).observe(el);
    document.addEventListener('visibilitychange', function () { if (document.hidden) vis = false; else { var r = el.getBoundingClientRect(); vis = r.bottom > -120 && r.top < innerHeight + 120; go(); } });
    el.__st = { R: R, scene: scene, cam: cam }; /* debugging handle */
    return { T: T, R: R, scene: scene, cam: cam, tex: tex, E: E, size: size, go: go, onFrame: function (f) { fns.push(f); }, onFit: function (f) { fitFns.push(f); } };
  }

  /* the device: metal body, black glass front, the real UI screen, an additive gloss pass */
  function device(T, o, E) {
    var G = new T.Group();
    var metal = E(new T.MeshPhysicalMaterial({ color: o.metal || 0x3b3e45, metalness: 1, roughness: .22, clearcoat: .7, clearcoatRoughness: .16, envMapIntensity: 1.25 }));
    var body = new T.Mesh(slab(T, o.w, o.h, o.r, o.d, o.d * .45), metal); G.add(body);
    /* o.side: colour of a softly lit side band (the extrude's side group), so a dark slab still reads on a dark page */
    if (o.side != null) body.material = [metal, E(new T.MeshPhysicalMaterial({ color: o.metal || 0x3b3e45, metalness: 1, roughness: .26, clearcoat: .6, clearcoatRoughness: .2, envMapIntensity: 1.2, emissive: o.side, emissiveIntensity: o.sideK == null ? .09 : o.sideK }))];
    var front = o.d / 2;
    var black = E(new T.MeshPhysicalMaterial({ color: 0x040405, metalness: 0, roughness: .1, clearcoat: o.plate == null ? 1 : o.plate, clearcoatRoughness: .04, envMapIntensity: o.plate == null ? .8 : o.plate }));
    var plate = new T.Mesh(flat(T, o.w - 2 * o.rim, o.h - 2 * o.rim, o.r - o.rim), black); plate.position.z = front + .002; G.add(plate);
    var scr = new T.Mesh(flat(T, o.w - 2 * o.bz, o.h - 2 * o.bz, Math.max(.01, o.r - o.bz)), new T.MeshBasicMaterial({ map: o.tex, toneMapped: false }));
    scr.position.z = front + .004; G.add(scr);
    var gloss = new T.Mesh(flat(T, o.w - 2 * o.rim, o.h - 2 * o.rim, o.r - o.rim), E(new T.MeshStandardMaterial({ color: 0xffffff, metalness: 1, roughness: .2, envMapIntensity: o.gloss == null ? .2 : o.gloss, transparent: true, blending: T.AdditiveBlending, depthWrite: false })));
    gloss.position.z = front + .006; G.add(gloss);
    /* transparent shadow catcher over the screen: floating cards / lifted layers drop soft shadows onto the panel */
    var catcher = new T.Mesh(flat(T, o.w - 2 * o.rim, o.h - 2 * o.rim, o.r - o.rim), new T.ShadowMaterial({ opacity: o.shadow == null ? .38 : o.shadow, transparent: true, depthWrite: false }));
    catcher.position.z = front + .005; catcher.receiveShadow = true; G.add(catcher);
    return { group: G, front: front, screen: scr, metal: metal };
  }

  /* ---------- HERO: tall panel + floating cards ---------- */
  function hero(T, el, c) {
    var st = stage(T, el, c), P = c.panel, S = 4 / P.h, W = P.w * S, H = P.h * S, bz = (c.bezel || 9) * S;
    var urls = [c.screen].concat((c.cards || []).map(function (k) { return k.src; }));
    return Promise.all(urls.map(st.tex)).then(function (tx) {
      var dev = device(T, { w: W + 2 * bz, h: H + 2 * bz, r: (P.r || 40) * S, d: (c.depth || 34) * S, rim: (c.rim || 3.2) * S, bz: bz, tex: tx[0], gloss: .045, metal: c.metal }, st.E);
      var root = new T.Group(); root.add(dev.group); st.scene.add(root);
      var edge = st.E(new T.MeshPhysicalMaterial({ color: 0x17181c, metalness: .35, roughness: .2, clearcoat: 1, clearcoatRoughness: .08, envMapIntensity: 1.1 }));
      var glossM = st.E(new T.MeshStandardMaterial({ color: 0xffffff, metalness: 1, roughness: .2, envMapIntensity: .03, transparent: true, blending: T.AdditiveBlending, depthWrite: false }));
      var cards = (c.cards || []).map(function (k, i) {
        var g = new T.Group(), w = k.w * S, h = k.h * S, r = (k.r || 12) * S, d = (k.d || 10) * S, pad = (k.pad || 3) * S;
        g.add(new T.Mesh(slab(T, w + 2 * pad, h + 2 * pad, r + pad, d, d * .45), edge));
        var f = new T.Mesh(flat(T, w, h, r), new T.MeshBasicMaterial({ map: tx[i + 1], toneMapped: false })); f.position.z = d / 2 + .003; g.add(f);
        var gl = new T.Mesh(flat(T, w + 2 * pad, h + 2 * pad, r + pad), glossM); gl.position.z = d / 2 + .005; g.add(gl);
        g.position.set(k.x * S, k.y * S, dev.front + k.z * S);
        g.rotation.set(k.rx || 0, k.ry || 0, k.rz || 0);
        g.userData = { base: g.position.clone(), ph: i * 1.9 + .4, amp: (k.amp || 6) * S, delay: .25 + i * .12 };
        casts(g); dev.group.add(g); return g;
      });
      keyLight(T, dev.group, Math.max(W, H) * .8, [-1.6, 2.4, 6]);
      var sh = blobShadow(T, c.glow); sh.rotation.x = -Math.PI / 2; st.scene.add(sh);
      var pose = c.pose || { rx: .08, ry: -.46, rz: .07 };
      var off = c.offset || { x: 0, y: 0 };
      function fit(w, h) {
        var vfov = st.cam.fov * Math.PI / 180, need = Math.max(H * 1.34, (W * 2.05) / st.cam.aspect);
        st.cam.position.set(0, 0, (need / 2) / Math.tan(vfov / 2));
        st.cam.lookAt(0, 0, 0);
        sh.position.set(off.x, -H * .62, 0); sh.scale.set(W * 2.1, W * .8, 1);
      }
      st.onFit(fit); st.size();
      /* card tips: hover (mouse) or tap shows a frosted note next to the card; the card lifts toward the viewer */
      var tipEl = document.createElement('div'); tipEl.className = 'afx3d-tip'; tipEl.setAttribute('role', 'status');
      tipEl.innerHTML = '<b></b><span></span>'; el.appendChild(tipEl);
      var ray = new T.Raycaster(), ndc = new T.Vector2(), hover = -1, pinned = -1, tv = new T.Vector3();
      var hits = cards.map(function (g) { var a = []; g.traverse(function (m) { if (m.isMesh) a.push(m); }); return a; });
      function pick(cx, cy) {
        var r = el.getBoundingClientRect(); ndc.set((cx - r.left) / r.width * 2 - 1, -((cy - r.top) / r.height) * 2 + 1);
        ray.setFromCamera(ndc, st.cam); var best = -1, bd = 1e9;
        hits.forEach(function (ms, i) { var h = ray.intersectObjects(ms, false); if (h.length && h[0].distance < bd) { bd = h[0].distance; best = i; } });
        return best;
      }
      function tip(i) {
        var k = (c.cards || [])[i];
        if (!k || !k.tip) { tipEl.classList.remove('on'); return; }
        tipEl.firstChild.textContent = k.tip.t || ''; tipEl.lastChild.textContent = k.tip.d || ''; tipEl.classList.add('on');
        el.classList.add('afx3d-touched');
      }
      el.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') return;
        var i = pick(e.clientX, e.clientY);
        if (i !== hover) { hover = i; el.style.cursor = i > -1 ? 'pointer' : ''; if (pinned < 0) { if (i > -1) tip(i); else tipEl.classList.remove('on'); } }
      }, { passive: true });
      el.addEventListener('pointerleave', function () { hover = -1; el.style.cursor = ''; if (pinned < 0) tipEl.classList.remove('on'); }, { passive: true });
      el.addEventListener('click', function (e) {
        var i = pick(e.clientX, e.clientY);
        if (i > -1 && pinned !== i) { pinned = i; tip(i); } else { pinned = -1; if (hover < 0 || i < 0) tipEl.classList.remove('on'); }
        st.go();
      });
      var mx = 0, my = 0, ax = 0, ay = 0, t0 = -1, scrollK = 0;
      var zone = el.closest('[data-afx3d-zone]') || el;
      if (window.matchMedia && matchMedia('(hover:hover) and (pointer:fine)').matches && !REDUCED) {
        zone.addEventListener('pointermove', function (e) { var r = el.getBoundingClientRect(); mx = clamp((e.clientX - r.left) / r.width) - .5; my = clamp((e.clientY - r.top) / r.height) - .5; st.go(); }, { passive: true });
        zone.addEventListener('pointerleave', function () { mx = 0; my = 0; }, { passive: true });
      }
      window.addEventListener('scroll', function () { var r = el.getBoundingClientRect(); scrollK = clamp(-r.top / Math.max(1, r.height)); }, { passive: true });
      var intro = c.intro !== false && !REDUCED; /* with a poster the scene starts in its final pose, so the crossfade is seamless */
      st.onFrame(function (t, dt) {
        if (t0 < 0) t0 = t;
        var e = intro ? outCubic(clamp((t - t0) / 1.8)) : 1;
        var k = Math.min(1, dt * 3.2); ax += (mx - ax) * k; ay += (my - ay) * k;
        var fl = REDUCED ? 0 : Math.sin(t * .8) * .055;
        root.rotation.set(pose.rx + ay * .16 + scrollK * .25, pose.ry + ax * .34 - (1 - e) * .7, pose.rz + ax * .02);
        root.position.set(off.x, off.y + fl - (1 - e) * 1.4 + scrollK * .9, 0);
        var act = pinned > -1 ? pinned : hover;
        cards.forEach(function (g, i) {
          var u = g.userData, ce = intro ? outCubic(clamp((t - t0 - u.delay) / 1.4)) : 1;
          u.hk = (u.hk || 0) + ((i === act ? 1 : 0) - (u.hk || 0)) * Math.min(1, dt * 10);
          g.position.x = u.base.x * (.6 + .4 * ce);
          g.position.y = u.base.y + (REDUCED ? 0 : Math.sin(t * 1.05 + u.ph) * u.amp * (1 - u.hk));
          g.position.z = dev.front + (u.base.z - dev.front) * ce + (1 - ce) * -.2 + u.hk * .22;
          g.scale.setScalar((.7 + .3 * ce) * (1 + .06 * u.hk));
        });
        if (act > -1 && tipEl.classList.contains('on')) {
          var k = c.cards[act], g = cards[act];
          tv.set(0, (k.h / 2 + (k.pad || 3)) * S, 0); g.localToWorld(tv); tv.project(st.cam);
          var w = el.clientWidth, h = el.clientHeight, x = (tv.x + 1) / 2 * w, y = (1 - tv.y) / 2 * h;
          var tw = tipEl.offsetWidth || 220; x = Math.max(tw / 2 + 6, Math.min(w - tw / 2 - 6, x));
          tipEl.style.transform = 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px) translate(-50%,calc(-100% - 12px))';
        }
        sh.material.opacity = .95 - Math.max(0, root.position.y - off.y) * .5;
      });
      st.go();
    });
  }

  /* ---------- DUO: two panels floating together (home hero: AppleFX + Cortex), pointer tilt, idle float ----------
     c.items[i] = { screen, panel:{w,h,r}, bezel, depth, rim, metal, side, sideK, glow, pos:{x,y,z}, rot:{x,y,z} } in panel px;
     c.span = {w,h} px the composition must fit; c.floor = px below centre where the glows sit. */
  function duo(T, el, c) {
    var st = stage(T, el, c), items = c.items || [], U = 4 / (c.unit || 560);
    return Promise.all(items.map(function (it) { return st.tex(it.screen); })).then(function (tx) {
      var root = new T.Group(); st.scene.add(root);
      var groups = items.map(function (it, i) {
        var P = it.panel, bz = (it.bezel || 8) * U;
        var dev = device(T, { w: P.w * U + 2 * bz, h: P.h * U + 2 * bz, r: (P.r || 34) * U, d: (it.depth || 30) * U, rim: (it.rim || 3) * U, bz: bz, tex: tx[i], gloss: it.gloss == null ? .05 : it.gloss, metal: it.metal, side: it.side, sideK: it.sideK }, st.E);
        var g = new T.Group(), p = it.pos || {}, r = it.rot || {};
        g.add(dev.group); g.position.set((p.x || 0) * U, (p.y || 0) * U, (p.z || 0) * U); g.rotation.set(r.x || 0, r.y || 0, r.z || 0);
        g.userData = { base: g.position.clone(), rot: g.rotation.clone(), ph: i * 2.3 + .6 };
        root.add(g); return g;
      });
      /* glows under the panels are optional (c.shadows:false turns them off) */
      var shs = items.map(function (it) { var sh = blobShadow(T, it.glow); sh.rotation.x = -Math.PI / 2; sh.visible = c.shadows !== false; st.scene.add(sh); return sh; });
      /* hoverable items (it.pick): a soft glowing outline fades in on hover, click dispatches 'afx3d:pick' on el */
      function haloTex(w, h, r, col) {
        var k = 256 / Math.max(w, h), pad = 40, cw = Math.round(w * k) + pad * 2, ch = Math.round(h * k) + pad * 2;
        var cv = document.createElement('canvas'); cv.width = cw; cv.height = ch; var x = cv.getContext('2d');
        x.strokeStyle = col; x.shadowColor = col;
        [[22, 7, .35], [10, 4, .7], [2, 2.2, 1]].forEach(function (p) { x.shadowBlur = p[0]; x.lineWidth = p[1]; x.globalAlpha = p[2]; x.beginPath(); x.roundRect(pad, pad, cw - pad * 2, ch - pad * 2, r * k); x.stroke(); });
        var t = new T.CanvasTexture(cv); t.colorSpace = T.SRGBColorSpace; return { t: t, sx: cw / (cw - pad * 2), sy: ch / (ch - pad * 2) };
      }
      var picks = [];
      items.forEach(function (it, i) {
        if (!it.pick) return;
        var P = it.panel, bz = (it.bezel || 8) * U, w = P.w * U + 2 * bz, h = P.h * U + 2 * bz, r = (P.r || 34) * U;
        var ht = haloTex(w, h, r, it.pick.color || '#5aa8ff');
        var halo = new T.Mesh(new T.PlaneGeometry(w * ht.sx, h * ht.sy), new T.MeshBasicMaterial({ map: ht.t, transparent: true, opacity: 0, depthWrite: false, toneMapped: false, blending: T.AdditiveBlending }));
        halo.position.z = (it.depth || 30) * U / 2 + .02; groups[i].children[0].add(halo);
        var meshes = []; groups[i].traverse(function (m) { if (m.isMesh && m !== halo) meshes.push(m); });
        picks.push({ i: i, g: groups[i], halo: halo, meshes: meshes, k: 0 });
      });
      var ray = new T.Raycaster(), ndc = new T.Vector2(), hov = -1;
      function pickAt(cx, cy) {
        var r = el.getBoundingClientRect(); ndc.set((cx - r.left) / r.width * 2 - 1, -((cy - r.top) / r.height) * 2 + 1);
        ray.setFromCamera(ndc, st.cam); var best = -1, bd = 1e9;
        picks.forEach(function (p, j) { var hh = ray.intersectObjects(p.meshes, false); if (hh.length && hh[0].distance < bd) { bd = hh[0].distance; best = j; } });
        return best;
      }
      if (picks.length) {
        el.addEventListener('pointermove', function (e) {
          var j = pickAt(e.clientX, e.clientY); if (j === hov) return; hov = j;
          el.style.cursor = j > -1 ? 'pointer' : ''; el.classList.toggle('afx3d-hover', j > -1);
          el.dispatchEvent(new CustomEvent('afx3d:hover', { detail: { index: j > -1 ? picks[j].i : -1 } })); st.go();
        }, { passive: true });
        el.addEventListener('pointerleave', function () { if (hov < 0) return; hov = -1; el.style.cursor = ''; el.classList.remove('afx3d-hover'); el.dispatchEvent(new CustomEvent('afx3d:hover', { detail: { index: -1 } })); });
        el.addEventListener('click', function (e) { var j = pickAt(e.clientX, e.clientY); if (j > -1) el.dispatchEvent(new CustomEvent('afx3d:pick', { detail: { index: picks[j].i, x: e.clientX, y: e.clientY } })); });
      }
      var span = c.span || { w: 900, h: 760 }, off = c.offset || { x: 0, y: 0 };
      function fit() {
        var vfov = st.cam.fov * Math.PI / 180, need = Math.max(span.h * U, (span.w * U) / st.cam.aspect);
        st.cam.position.set(0, 0, (need / 2) / Math.tan(vfov / 2)); st.cam.lookAt(0, 0, 0);
        items.forEach(function (it, i) { var p = it.pos || {}; shs[i].position.set((p.x || 0) * U + off.x * U, -(c.floor || 330) * U, (p.z || 0) * U); shs[i].scale.set(it.panel.w * 2.3 * U, it.panel.w * .62 * U, 1); });
      }
      st.onFit(fit); st.size();
      var mx = 0, my = 0, ax = 0, ay = 0;
      var zone = el.closest('[data-afx3d-zone]') || el;
      if (window.matchMedia && matchMedia('(hover:hover) and (pointer:fine)').matches && !REDUCED) {
        zone.addEventListener('pointermove', function (e) { var r = el.getBoundingClientRect(); mx = clamp((e.clientX - r.left) / r.width) - .5; my = clamp((e.clientY - r.top) / r.height) - .5; st.go(); }, { passive: true });
        zone.addEventListener('pointerleave', function () { mx = 0; my = 0; }, { passive: true });
      }
      st.onFrame(function (t, dt) {
        var k = Math.min(1, dt * 3); ax += (mx - ax) * k; ay += (my - ay) * k;
        root.rotation.set(ay * .14, ax * .3, 0); root.position.set(off.x * U, off.y * U, 0);
        groups.forEach(function (g, i) {
          var u = g.userData, f = REDUCED ? 0 : Math.sin(t * .75 + u.ph);
          g.position.y = u.base.y + f * .06; g.rotation.x = u.rot.x + f * .025; g.rotation.z = u.rot.z + (REDUCED ? 0 : Math.cos(t * .6 + u.ph) * .012);
          shs[i].material.opacity = .9 - f * .12;
        });
        picks.forEach(function (p, j) {
          p.k += ((j === hov ? 1 : 0) - p.k) * Math.min(1, dt * 9);
          p.halo.material.opacity = p.k; var sc = 1 + p.k * .025; p.g.scale.setScalar(sc);
          p.g.position.z = p.g.userData.base.z + p.k * .12;
        });
      });
      st.go();
    });
  }

  /* ---------- LAYERS: real layers as glass slabs docking on scroll ---------- */
  function layers(T, el, c) {
    var st = stage(T, el, c), P = c.panel, S = (c.unit || 6) / P.w, W = P.w * S, H = P.h * S, bz = (c.bezel || 10) * S;
    var L = c.layers || [];
    return Promise.all([c.base].concat(L.map(function (l) { return l.src; })).map(st.tex)).then(function (tx) {
      var dev = device(T, { w: W + 2 * bz, h: H + 2 * bz, r: (P.r || 22) * S, d: (c.depth || 22) * S, rim: (c.rim || 3) * S, bz: bz, tex: tx[0], gloss: c.gloss == null ? .035 : c.gloss, plate: c.plate }, st.E);
      var root = new T.Group(); root.add(dev.group); st.scene.add(root);
      var edge = st.E(new T.MeshPhysicalMaterial({ color: 0x23252b, metalness: .3, roughness: .16, clearcoat: 1, clearcoatRoughness: .06, envMapIntensity: 1.2 }));
      var glossM = st.E(new T.MeshStandardMaterial({ color: 0xffffff, metalness: 1, roughness: .2, envMapIntensity: c.gloss == null ? .028 : c.gloss * .8, transparent: true, blending: T.AdditiveBlending, depthWrite: false }));
      var parts = L.map(function (l, i) {
        var g = new T.Group(), w = l.w * S, h = l.h * S, r = (l.r || 0) * S + .004, d = (l.d || 8) * S;
        g.add(new T.Mesh(slab(T, w, h, r, d, d * .45), edge));
        var f = new T.Mesh(flat(T, w, h, r), new T.MeshBasicMaterial({ map: tx[i + 1], toneMapped: false })); f.position.z = d / 2 + .002; g.add(f);
        var gl = new T.Mesh(flat(T, w, h, r), glossM); gl.position.z = d / 2 + .004; g.add(gl);
        var bx = (l.x + l.w / 2 - P.w / 2) * S, by = (P.h / 2 - (l.y + l.h / 2)) * S;
        g.userData = { bx: bx, by: by, bz: dev.front + d / 2 + .003, lift: (l.lift || 200) * S, dx: (l.dx || 0) * S, dy: (l.dy || 0) * S, order: l.order == null ? i : l.order };
        g.position.set(bx, by, g.userData.bz);
        casts(g); dev.group.add(g); return g;
      });
      keyLight(T, dev.group, Math.max(W, H) * .75, [-1.2, 2.6, 7], c.keyLight);
      var sh = blobShadow(T); sh.rotation.x = -Math.PI / 2; st.scene.add(sh);
      var ex = c.exploded || { rx: -.9, ry: 0, rz: -.55 };
      function fit(w, h) {
        var vfov = st.cam.fov * Math.PI / 180, need = Math.max(H * 1.5, (W * 1.18) / st.cam.aspect);
        st.cam.position.set(0, 0, (need / 2) / Math.tan(vfov / 2)); st.cam.lookAt(0, 0, 0);
      }
      st.onFit(fit); st.size();
      var cur = 0, n = parts.length, span = .62 / Math.max(1, n);
      st.onFrame(function (t, dt) {
        var target = REDUCED ? 1 : clamp(el.__p || 0);
        cur += (target - cur) * Math.min(1, dt * 7); if (Math.abs(target - cur) < 1e-4) cur = target;
        parts.forEach(function (g) {
          var u = g.userData, k = 1 - ease(clamp((cur - .06 - u.order * span) / (span * 1.35)));
          g.position.set(u.bx + u.dx * k, u.by + u.dy * k, u.bz + u.lift * k);
          g.rotation.set(0, 0, 0);
        });
        var f = ease(clamp((cur - .5) / .45));
        var se = c.settle || { rx: 0, ry: 0, rz: 0 }; /* assembled pose (default: face-on) */
        root.rotation.set(ex.rx * (1 - f) + se.rx * f, ex.ry * (1 - f) + se.ry * f, ex.rz * (1 - f) + se.rz * f);
        var sc = .84 + .16 * f; root.scale.setScalar(sc);
        root.position.y = (1 - f) * -.32 + (REDUCED ? 0 : Math.sin(t * .7) * .03 * (1 - f));
        sh.position.set(0, -H * (.62 - .2 * (1 - f)), 0); sh.scale.set(W * 1.5 * sc, H * .9 * sc, 1);
        sh.material.opacity = .55 + .35 * f;
      });
      st.go();
    });
  }

  function boot(el) {
    if (el.__afx3d) return; el.__afx3d = 1;
    var c, attr = el.clientWidth < 700 && el.hasAttribute('data-afx3d-m') ? 'data-afx3d-m' : 'data-afx3d';
    try { c = JSON.parse(el.getAttribute(attr)); } catch (e) { return; }
    var probe = document.createElement('canvas');
    if (!(probe.getContext('webgl2') || probe.getContext('webgl'))) { el.classList.add('afx3d-fail'); return; }
    three().then(function (T) { return (c.mode === 'layers' ? layers : c.mode === 'duo' ? duo : hero)(T, el, c); })
      .catch(function (e) { el.classList.add('afx3d-fail'); if (window.console) console.warn('afx3d', e); });
  }
  /* "eager" scenes (heroes with a poster still) start after the page has loaded and the main thread is idle, so
     three.js, the shaders and the environment map never compete with the first paint; the poster covers the wait */
  function afterLoad(fn) {
    function go() { if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 1200 }); else setTimeout(fn, 150); }
    if (document.readyState === 'complete') go(); else window.addEventListener('load', go, { once: true });
  }
  function scan() {
    [].forEach.call(document.querySelectorAll('[data-afx3d]'), function (el) {
      if (el.__afx3dObs) return; el.__afx3dObs = 1;
      if (el.hasAttribute('data-afx3d-eager') || !('IntersectionObserver' in window)) return afterLoad(function () { boot(el); });
      var io = new IntersectionObserver(function (es) { if (es[0].isIntersecting) { io.disconnect(); boot(el); } }, { rootMargin: '700px 0px' });
      io.observe(el);
    });
  }
  /* lib: the building blocks, used by the offline turntable renders (catalog GIFs) */
  window.AFX3D = { scan: scan, boot: boot, lib: { three: three, rr: rr, flat: flat, slab: slab, studioEnv: studioEnv, device: device, blobShadow: blobShadow } };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
  document.addEventListener('shopify:section:load', scan);
})();
